import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get, isEmpty } from 'lodash';

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

import {
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';

import './SubsidyReceipt.css';

import nimbusLogo from '../../nimbus_logo.png';

const SubsidyReceipt = props => {
  const [address1, setAddress1] = React.useState('');
  const [address2, setAddress2] = React.useState('');
  const [loading, setLoading] = React.useState(null);

  const onSave = async () => {
    setLoading(0);

    const margin = 15;
    const pdfWidth = 595.5;
    const pdfHeight = 842.25;
    let currentOffset = document.getElementById('field_container').clientHeight;

    const zip = new JSZip();

    for (let i = 0; i < props.data.length; i++) {
      setLoading(i + 1);
      const student = props.data[i];
      const html = document.getElementById(
        `subsidy_receipt_content_${student.studentId}`,
      );

      const pdf = new jsPDF({ unit: 'px', format: [pdfWidth, pdfHeight] });
      // const pdf = new jsPDF({ unit: 'px' });
      if (pdf) {
        const htmlRect = html.getBoundingClientRect();
        const canvas = await html2canvas(html, {
          width: html.clientWidth,
          height: html.clientHeight,
          x: 0 + htmlRect.left,
          y: htmlRect.top,
          useCORS: true,
        });

        currentOffset += html.clientHeight;
        const imgData = canvas.toDataURL('image/png');
        const imgProps = pdf.getImageProperties(imgData);
        const pdfW = pdf.internal.pageSize.getWidth() - 2 * margin;
        let pdfH = (imgProps.height * pdfW) / imgProps.width;

        if (pdfH <= pdfHeight - 2 * margin) {
          pdf.addImage(imgData, 'PNG', margin, margin, pdfW, pdfH);
        } else {
          const totalHeight = 1123 - 2 * margin;
          const totalPages = Math.ceil(canvas.height / 2 / totalHeight);

          for (let j = 0; j < totalPages; j++) {
            const pageCanvas = document.createElement('canvas');
            pageCanvas.width = canvas.width;

            // calculate new height;
            let h =
              j === totalPages - 1
                ? canvas.height / 2 - totalHeight * j
                : totalHeight;

            pageCanvas.height = h * 2;

            pageCanvas.style.width = canvas.style.width;
            pageCanvas.style.height = `${h}px`;

            const pageContext = pageCanvas.getContext('2d');

            pageContext.drawImage(
              canvas,
              0,
              totalHeight * j * 2,
              canvas.width,
              h * 2,
              0,
              0,
              pageCanvas.width,
              h * 2,
            );

            const pageData = pageCanvas.toDataURL('image/png');
            const pageImgProps = pdf.getImageProperties(pageData);
            const pagePdfW = pdf.internal.pageSize.getWidth() - 2 * margin;
            const pagePdfH =
              (pageImgProps.height * pagePdfW) / pageImgProps.width;
            pdf.addImage(pageData, 'PNG', margin, margin, pagePdfW, pagePdfH);
            if (j < totalPages - 1) pdf.addPage();
          }
        }
      }
      zip.file(`${student.studentName}.pdf`, pdf.output('blob'));
    }

    const content = await zip.generateAsync({ type: 'blob' });
    saveAs(content, 'creditted_lessons_receipts.zip');
    setLoading(null);
  };

  const header = () => (
    <div className="subsidy_receipt_header_container">
      <Typography
        classes={{ colorInherit: 'subsidy_receipt_text_white' }}
        variant="title"
        color="inherit"
      >
        {'RECEIPT'}
      </Typography>
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <div style={{ marginRight: '8px' }}>
          <Typography
            classes={{ colorInherit: 'subsidy_receipt_text_white' }}
            color="inherit"
          >
            {'Date:'}
          </Typography>
        </div>
        <Typography
          classes={{
            colorInherit: 'subsidy_receipt_text_white',
            root: 'subsidy_receipt_text_bold',
          }}
          color="inherit"
        >
          {new Date().toLocaleString('en-CA', {
            month: 'short',
            day: '2-digit',
            year: 'numeric',
          })}
        </Typography>
      </div>
    </div>
  );

  const orgInfo = (student, logo) => (
    <div className="subsidy_receipt_section subsidy_receipt_org_info_container">
      <img className="subsidy_receipt_logo" src={logo || nimbusLogo} />
      <div className="subsidy_receipt_org_info_item">
        <Typography>{`${student.studentUniversity ||
          'University Name'}/${props.organizationName ||
          'Organization'}`}</Typography>
        <Typography>{props.adminName || 'Program Manager Name'}</Typography>
      </div>
      <div className="subsidy_receipt_org_info_item">
        <Typography>{address1 || 'Address'}</Typography>
        <Typography>{address2 || 'City, Province Postal Code'}</Typography>
      </div>
    </div>
  );

  const studentInfo = student => (
    <div className="subsidy_receipt_section subsidy_receipt_student_info_container">
      <div>
        <Typography>{'Name of student:'}</Typography>
        <Typography>{student.studentName}</Typography>
      </div>
      <div className="subsidy_receipt_total">
        <div style={{ marginRight: '8px' }}>
          <Typography color="textSecondary">{'TOTAL CREDITS USED:'}</Typography>
        </div>
        <Typography
          variant="display1"
          classes={{
            root: 'subsidy_receipt_total_credit_text',
          }}
        >{`$${(student.creditsUsed / 100).toFixed(2)}`}</Typography>
      </div>
    </div>
  );

  const lessons = student => {
    const cellHeader = label => (
      <TableCell
        padding="dense"
        classes={{
          root: 'subsidy_receipt_cell_colored subsidy_receipt_text_bold',
        }}
      >
        {label}
      </TableCell>
    );
    return (
      <Table>
        <TableHead>
          <TableRow>
            {cellHeader('Item/Course')}
            {cellHeader('Tutor')}
            {cellHeader('Lesson date')}
            {cellHeader('Duration')}
            {cellHeader('Hourly rate')}
            {cellHeader('Credits used')}
          </TableRow>
        </TableHead>
        <TableBody>
          {student.lessons.map(l => (
            <TableRow key={l._id}>
              <TableCell padding="dense">{l.course}</TableCell>
              <TableCell padding="dense">{l.tutor}</TableCell>
              <TableCell padding="dense">{l.date}</TableCell>
              <TableCell padding="dense">{`${l.hours} hrs`}</TableCell>
              <TableCell padding="dense">{`$${(l.rate / 100).toFixed(
                2,
              )}`}</TableCell>
              <TableCell padding="dense">{`$${(l.creditsUsed / 100).toFixed(
                2,
              )}`}</TableCell>
            </TableRow>
          ))}
        </TableBody>
        <TableFooter>
          <TableRow>
            {cellHeader('')}
            {cellHeader('')}
            {cellHeader('')}
            {cellHeader('')}
            {cellHeader('TOTAL')}
            {cellHeader(`$${(student.creditsUsed / 100).toFixed(2)}`)}
          </TableRow>
        </TableFooter>
      </Table>
    );
  };

  const signature = () => (
    <div>
      <div className="subsidy_receipt_box subsidy_receipt_signature_box" />
      <Typography>{'Student Signature'}</Typography>
      <div className="subsidy_receipt_box subsidy_receipt_signature_box" />
      <Typography>{'Program Manager Signature'}</Typography>
    </div>
  );

  const dateField = () => (
    <div>
      <div className="subsidy_receipt_box subsidy_receipt_date_box" />
      <Typography>{'Date'}</Typography>
      <div className="subsidy_receipt_box subsidy_receipt_date_box" />
      <Typography>{'Date'}</Typography>
    </div>
  );

  return (
    <div id="sr_container">
      <div id="field_container" style={{ padding: 24, width: 764 }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginRight: 24,
            marginBottom: 20,
          }}
        >
          <TextField
            id="address1"
            label="Address"
            value={address1}
            onChange={e => setAddress1(e.target.value)}
            margin="normal"
          />
          <TextField
            id="address2"
            label="City, Province Postal Code"
            value={address2}
            onChange={e => setAddress2(e.target.value)}
            margin="normal"
          />
        </div>
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          <Button
            disabled={loading != null}
            variant="raised"
            color="primary"
            onClick={onSave}
          >
            {"SAVE AS PDF'S"}
          </Button>
          {loading && (
            <React.Fragment>
              <CircularProgress className="subsidy_receipt_loader" />
              <Typography>
                {`${Math.round((loading / props.data.length) * 100)}%`}
              </Typography>
            </React.Fragment>
          )}
        </div>
      </div>
      {props.data.map(student => (
        <div
          key={student.studentId}
          id={`subsidy_receipt_content_${student.studentId}`}
          className="subsidy_receipt_content"
        >
          {header()}
          <div className="subsidy_receipt_body">
            {orgInfo(student, props.organizationLogo)}
            {studentInfo(student)}
            {lessons(student)}
            <div style={{ display: 'flex', flexDirection: 'row' }}>
              {signature()}
              {dateField()}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

SubsidyReceipt.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      studentId: PropTypes.string.isRequired,
      studentName: PropTypes.string.isRequired,
      studentUniversity: PropTypes.string.isRequired,
      creditsUsed: PropTypes.number.isRequired,
      lessons: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.string.isRequired,
          course: PropTypes.string.isRequired,
          tutor: PropTypes.string.isRequired,
          date: PropTypes.string.isRequired,
          hours: PropTypes.number.isRequired,
          rate: PropTypes.number.isRequired,
          creditsUsed: PropTypes.number.isRequired,
        }),
      ).isRequired,
    }),
  ).isRequired,
};

export default connect((state, props) => {
  const selected = get(state, 'auth.selectedOrganization');
  const organization =
    (get(state, 'auth.data.organizations') || []).find(
      o => o._id === selected,
    ) || {};

  return {
    organizationName: get(organization, 'name'),
    organizationLogo: get(organization, 'logo'),
    adminName: !isEmpty(get(state, 'auth.data.user'))
      ? `${get(state, 'auth.data.user.user.first')} ${get(
          state,
          'auth.data.user.user.last',
        )}`
      : null,
  };
})(SubsidyReceipt);



// WEBPACK FOOTER //
// ./src/app/components/SubsidyReceipt.js